import { Fragment, useEffect, useMemo, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, ChevronLeftIcon, PaperAirplaneIcon, PlusIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { useApprovalQueue, useOrder, useRejectOrder, useRemoveOrderLine, useSendSupplier, useSetOrderLineQuantity, useTrackingOptions, useLineAvailability, useAddOrderLine, useApproverCatalogue, useCancelPurchaseOrder } from '../hooks/useStockOrders';
import { useCurrentUser } from '../hooks/useCurrentUser';

const money = (v) => `£${Number(v || 0).toFixed(2)}`;

// The PO as a PDF to send the supplier (04/11/2026) - what a "No approval
// required" call-off needs, since it never goes through Xero / ApprovalMax.
async function downloadPoPdf(orderId, po) {
  const res = await api.get(`/api/stock-orders/${orderId}/purchase-orders/${po.id}/pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${po.po_number || `PO-${po.id}`}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Who may be named as the PO requester in ApprovalMax. The name is appended to
// the Xero PO reference as "· Req: {name}" so the approver sees who asked for
// it -- free text, not a validated ApprovalMax field, so this list is policy
// rather than a technical constraint.
const REQUESTERS = ['Gavin Esterhuizen', 'Luschaan Corrigan', 'JJ King'];

// An editable quantity.
//
// Commits on blur or Enter rather than on every keystroke: typing "12" over a
// "3" passes through "1", and saving that would send a supplier one of
// something for the moment between keys.
//
// The storeman's original figure stays under the box once it differs, because
// an approver buying 20 where 12 was asked for is a decision somebody may need
// to see later.
function QtyCell({ orderId, line, onDone }) {
  const setQty = useSetOrderLineQuantity();
  const [value, setValue] = useState(String(line.quantity));

  useEffect(() => { setValue(String(line.quantity)); }, [line.quantity]);

  const commit = async () => {
    const n = Math.round(Number(value));
    if (!Number.isFinite(n) || n < 1) { setValue(String(line.quantity)); return; }
    if (n === Number(line.quantity)) return;
    try {
      const r = await setQty.mutateAsync({ id: orderId, line_id: line.id, quantity: n });
      if (!r.unchanged) onDone({ ok: true, msg: `${line.name}: ${r.was} → ${r.now}.` });
    } catch (e) {
      setValue(String(line.quantity));
      onDone({ ok: false, msg: e?.response?.data?.error || 'Could not change that quantity.' });
    }
  };

  const original = line.quantity_original == null ? null : Number(line.quantity_original);
  return (
    <div className="flex flex-col items-end">
      <input
        type="number"
        min="1"
        value={value}
        disabled={setQty.isPending}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        className="w-16 rounded-md border border-gray-300 px-2 py-1 text-right text-sm tabular-nums focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-50"
      />
      {original != null && original !== Number(line.quantity) ? (
        <span className="mt-0.5 text-[11px] text-gray-400">asked for {original}</span>
      ) : null}
    </div>
  );
}

// What the company already holds of this item, so the approver can decide
// whether to buy it at all.
//
// ORD-00026 asked for 12 Silicone Clear while Swindon held 47 and Birmingham
// 71, and for 3 tool bags while Bradford had 73. Eight of its twelve lines were
// already covered at the delivery storeroom — none of it visible from here.
function Availability({ lineId }) {
  const { data, isLoading } = useLineAvailability(lineId);
  if (isLoading || !data) return <p className="px-4 py-3 text-xs text-gray-400">Checking other storerooms…</p>;

  const rooms = data.storerooms || [];
  const covered = data.at_delivery_storeroom >= data.ordered;
  const elsewhere = data.total_on_hand - data.at_delivery_storeroom;
  const max = Math.max(1, ...rooms.map((r) => Math.abs(r.qty)));
  // Averaged over storerooms that hold any, matching the row badge — a site
  // that never stocks the item would otherwise drag the mean down and make an
  // ordinary holding look like a surplus.
  const avg = rooms.length ? rooms.reduce((t, r) => t + r.qty, 0) / rooms.length : 0;
  const top = rooms.reduce((b, r) => (!b || r.qty > b.qty ? r : b), null);

  return (
    <div className="px-4 py-3">
      <p className="text-xs text-gray-600">
        {covered ? (
          <span className="font-semibold text-amber-700">
            {data.delivery_location} already holds {data.at_delivery_storeroom} — more than the {data.ordered} ordered.
          </span>
        ) : data.at_delivery_storeroom > 0 ? (
          <>
            {data.delivery_location} holds <span className="font-semibold">{data.at_delivery_storeroom}</span> of the {data.ordered} ordered
            {elsewhere > 0 ? <>, with <span className="font-semibold">{elsewhere}</span> elsewhere.</> : '.'}
          </>
        ) : elsewhere > 0 ? (
          <>None at {data.delivery_location}, but <span className="font-semibold">{elsewhere}</span> elsewhere — a transfer may beat a purchase.</>
        ) : (
          <>None anywhere. This one genuinely needs buying.</>
        )}
        {/* Said even when the delivery storeroom is covered: "we don't need to
            buy it" and "somebody is sitting on a pile of it" are different
            facts, and the second outlives this order. */}
        {top && top.qty > avg * 2 && !top.is_delivery ? (
          <span className="ml-1 text-sky-700">
            {top.name} is holding {top.qty}, against an average of {avg.toFixed(1)} a storeroom.
          </span>
        ) : null}
        {/* Cable codes are summed across the group: "we have none" is usually
            "we have plenty under the other code". */}
        {data.grouped_as ? (
          <span className="ml-1 text-gray-400">Counted across {data.grouped_as}.</span>
        ) : null}
      </p>

      {rooms.length ? (
        <ul className="mt-2 space-y-1">
          {rooms.map((r) => (
            <li key={r.id} className="flex items-center gap-2">
              <span className={`w-36 shrink-0 truncate text-xs ${r.is_delivery ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                {r.name}{r.is_delivery ? ' (here)' : ''}
              </span>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                <div
                  className={`h-full rounded-full ${r.qty < 0 ? 'bg-amber-400' : r.is_delivery ? 'bg-brand' : 'bg-gray-300'}`}
                  style={{ width: `${(Math.abs(r.qty) / max) * 100}%` }}
                />
              </div>
              <span className={`w-12 shrink-0 text-right text-xs tabular-nums ${r.qty < 0 ? 'text-amber-700' : 'text-gray-700'}`}>
                {r.qty}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// Put an item on the order that the storeman left off. Search, pick, set a
// quantity, add; the new line then takes its supplier and price like any other.
// Before this an approver could only change or remove what was already there,
// so a missing item meant rejecting the order or raising a second one.
function AddItem({ orderId, onDone }) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState('1');
  const { data: results, isFetching } = useApproverCatalogue(picked ? '' : q);
  const add = useAddOrderLine();

  const reset = () => { setQ(''); setPicked(null); setQty('1'); };
  const submit = async () => {
    const n = Math.round(Number(qty));
    if (!picked || !Number.isFinite(n) || n < 1) return;
    try {
      await add.mutateAsync({
        id: orderId,
        product_id: picked.product_id || undefined,
        storeroom_item_id: picked.storeroom_item_id || undefined,
        quantity: n,
      });
      onDone({ ok: true, msg: `${picked.name} × ${n} added to the order.` });
      reset();
    } catch (e) {
      onDone({ ok: false, msg: e?.response?.data?.error || 'Could not add that item.' });
    }
  };

  const term = q.trim();
  return (
    <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
      <p className="mb-2 text-xs font-medium text-gray-500">Add an item</p>
      {picked ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800">
            {picked.name} <span className="text-xs text-gray-400">{picked.item_code}</span>
          </span>
          <input
            type="number"
            min={1}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-right text-sm tabular-nums"
            aria-label="Quantity"
          />
          <button
            type="button"
            onClick={submit}
            disabled={add.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-50"
          >
            <PlusIcon className="h-4 w-4" /> {add.isPending ? 'Adding…' : 'Add'}
          </button>
          <button type="button" onClick={reset} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
        </div>
      ) : (
        <div className="max-w-md">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by SKU or name…"
            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm"
          />
          {/* In the flow, not a floating dropdown: the table wrapper scrolls
              sideways, and overflow there would clip an absolute list. */}
          {term.length >= 2 && (
            <ul className="mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-gray-200 bg-white">
              {(results || []).map((it) => (
                <li key={it.storeroom_item_id ? `c-${it.storeroom_item_id}` : `p-${it.product_id}`}>
                  <button
                    type="button"
                    onClick={() => setPicked(it)}
                    className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50"
                  >
                    <span className="text-gray-800">
                      {it.name} <span className="text-xs text-gray-400">{it.item_code}</span>
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-gray-500">
                      from {money(it.unit_price)}
                    </span>
                  </button>
                </li>
              ))}
              {!isFetching && results && results.length === 0 && (
                <li className="px-3 py-2 text-sm text-gray-400">No orderable item matches “{term}”.</li>
              )}
              {isFetching && !results && <li className="px-3 py-2 text-sm text-gray-400">Searching…</li>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function OrderDetail({ orderId, onBack }) {
  const { data, isLoading } = useOrder(orderId);
  const reject = useRejectOrder();
  const removeLine = useRemoveOrderLine();
  // Which line's availability panel is open. One at a time: the panel is a
  // lookup across every storeroom, and opening all twelve at once would be a
  // dozen queries to answer a question about one item.
  const [openLine, setOpenLine] = useState(null);
  const send = useSendSupplier();
  const cancelPo = useCancelPurchaseOrder();
  // Why each supplier's PO is being raised - required, and the PO's first line.
  const [poMotivation, setPoMotivation] = useState({});
  const { data: tracking } = useTrackingOptions();
  const { data: me } = useCurrentUser();
  const [requester, setRequester] = useState('');
  const [contract, setContract] = useState('');
  // Per-line contract overrides, keyed by line id. Xero allows exactly one
  // Contract per line, so an order spanning several contracts has to say so
  // line by line; blank means it inherits the PO-level choice above.
  const [lineContracts, setLineContracts] = useState({});
  const [area, setArea] = useState('');
  // Account (GL code) for Xero / ApprovalMax (TKT-00070): PO-level choice plus a
  // per-line override, like Contract. Blank = the SKU's catalogue account.
  const [account, setAccount] = useState('');
  const [lineAccounts, setLineAccounts] = useState({});
  const [poComments, setPoComments] = useState({});
  const [choice, setChoice] = useState({});
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [flash, setFlash] = useState(null);
  const [sendingSupplier, setSendingSupplier] = useState(null);
  // "No approval required" (TKT-00071): which supplier card has the call-off
  // form open, and the reference typed on each.
  const [noApprovalOpen, setNoApprovalOpen] = useState(null);
  const [callOffRef, setCallOffRef] = useState({});
  // Which sent PO has its "what happens to the lines?" panel open.
  const [cancelFor, setCancelFor] = useState(null);

  useEffect(() => {
    if (!data?.lines) return;
    // Fill in a default supplier only for lines that don't have one chosen yet,
    // and keep whatever the user has already picked. This effect re-runs on
    // every `data` change — including the refetch after a quantity edit — so
    // rebuilding the whole map here would reset every line's supplier back to
    // its preferred one the moment any one quantity was changed.
    setChoice((prev) => {
      const next = { ...prev };
      for (const l of data.lines) {
        if (next[l.id]) continue;
        const pref = l.available_suppliers.find((s) => s.is_preferred) || l.available_suppliers[0];
        if (pref) next[l.id] = pref.supplier_id;
      }
      return next;
    });
  }, [data]);

  useEffect(() => {
    if (me?.display_name && REQUESTERS.includes(me.display_name)) setRequester((prev) => prev || me.display_name);
  }, [me]);

  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const pos = useMemo(() => {
    if (!data?.lines) return [];
    const readOnly = data.order.status !== 'submitted';
    const map = new Map();
    for (const l of data.lines) {
      if (l.removed_at) continue; // taken off the order — not going to any supplier
      const sid = readOnly ? l.supplier_id : choice[l.id];
      if (!sid) continue;
      const sup = l.available_suppliers.find((s) => s.supplier_id === sid);
      const unit = readOnly ? l.unit_price : sup?.unit_price;
      if (!map.has(sid)) map.set(sid, { supplier_id: sid, supplier_name: sup?.supplier_name || `Supplier ${sid}`, lines: [], subtotal: 0 });
      const grp = map.get(sid);
      grp.lines.push({ id: l.id, name: l.name, quantity: l.quantity, unit, min: readOnly ? null : (sup?.min_order_qty || null),
        catalogAccount: (l.storeroom_item_id ? l.storeroom_account : l.product_account) || null });
      grp.subtotal += Number(unit || 0) * l.quantity;
    }
    return Array.from(map.values());
  }, [data, choice]);

  if (isLoading || !data) return <div className="p-6 text-sm text-gray-400">Loading…</div>;
  const { order, lines, purchase_orders: generated } = data;
  const readOnly = order.status !== 'submitted';
  // Reopened by a cancelled PO (TKT-00117): only the lines not on a live PO
  // still need a decision, so those are the ones marked "to send".
  const reopened = !readOnly && (generated || []).some((g) => g.status === 'cancelled');
  const grandTotal = pos.reduce((t, p) => t + p.subtotal, 0);
  const accountLabel = (code) => {
    if (!code) return '';
    const a = (tracking?.accounts || []).find((x) => x.code === code);
    return a ? `${a.code} ${a.name}` : code;
  };
  const sentFor = (supplierId) => (generated || []).find((g) => g.supplier_id === supplierId && ['sent', 'approved'].includes(g.status));

  const sendSupplier = async (po, { noApproval = false } = {}) => {
    setSendingSupplier(po.supplier_id); setFlash(null);
    try {
      const ref = (callOffRef[po.supplier_id] || '').trim();
      const r = await send.mutateAsync({ id: order.id, supplier_id: po.supplier_id, line_ids: po.lines.map((l) => l.id), requester, contract, area,
        line_contracts: Object.fromEntries(po.lines.map((l) => [l.id, lineContracts[l.id]]).filter(([, v]) => v)), comments: poComments[po.supplier_id] || undefined, motivation: (poMotivation[po.supplier_id] || '').trim(),
        account: account || undefined, line_accounts: Object.fromEntries(po.lines.map((l) => [l.id, lineAccounts[l.id]]).filter(([, v]) => v)),
        ...(noApproval ? { no_approval: true, call_off_ref: ref } : {}) });
      setNoApprovalOpen(null);
      setFlash({ ok: true, msg: noApproval
        ? `${po.supplier_name}: ${r.po_number} created without approval (call-off ${ref}). It is ready in Goods Receiving and was not sent to Xero or ApprovalMax.`
        : `${po.supplier_name}: sent to ApprovalMax (${r.po_number}).` });
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.error || `Could not send ${po.supplier_name}.` });
    } finally { setSendingSupplier(null); }
  };
  // Cancel a PO already sent to Xero / ApprovalMax - deleted in Xero, cancelled
  // here. The panel opened by "Cancel PO" asks what happens to its lines
  // (TKT-00117): drop them, and the order stays approved; or keep them to send
  // again, and the order reopens. It cannot be undone, hence the panel.
  const cancelSentPo = async (po, gen, dropLines) => {
    setFlash(null);
    try {
      const r = await cancelPo.mutateAsync({ id: order.id, po_id: gen.id, drop_lines: dropLines });
      setCancelFor(null);
      const where = gen.no_approval ? '' : ' - deleted in Xero';
      setFlash({ ok: true, msg: !dropLines
        ? `${gen.po_number} cancelled${where}. Its lines can be changed or sent again.`
        : r.order_status === 'cancelled'
          ? `${gen.po_number} cancelled${where}. Its lines were removed, and with nothing left to order the order is cancelled.`
          : r.order_status === 'approved'
            ? `${gen.po_number} cancelled${where}. Its lines were removed and the order stays approved.`
            : `${gen.po_number} cancelled${where}. Its lines were removed; other lines on the order still need sending.` });
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.error || `Could not cancel ${gen.po_number}.` });
    }
  };
  // Removing is not a delete: the line stays on the order, struck through, and
  // the storeman who raised it sees that it went and who took it off. That also
  // makes it safe to undo, so this is a toggle rather than a one-way door.
  const toggleLine = async (l) => {
    const restore = !!l.removed_at;
    setFlash(null);
    try {
      const r = await removeLine.mutateAsync({ id: order.id, line_id: l.id, restore });
      setFlash({ ok: true, msg: restore ? `${l.name} put back on the order.`
        : r?.order_status === 'approved' ? `${l.name} removed. Everything left is already ordered, so the order is approved again.`
          : `${l.name} removed — ${order.requested_by_name} will see it struck through.` });
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.error || 'Could not change that item.' });
    }
  };

  const doReject = async () => { await reject.mutateAsync({ id: order.id, reason }); onBack(); };

  return (
    <div>
      <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to queue
      </button>

      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{order.reference || `Order #${order.id}`}</h2>
          <p className="text-sm text-gray-500">Raised by {order.requested_by_name} · deliver to {order.delivery_location || 'no location'}</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${order.status === 'submitted' ? 'bg-amber-100 text-amber-800' : order.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>{order.status}</span>
      </div>
      {order.notes && <p className="mb-4 rounded bg-gray-50 px-3 py-2 text-sm text-gray-600">{order.notes}</p>}
      {flash && <div className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${flash.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-700'}`}>{flash.msg}</div>}
      {reopened && (
        <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2.5 text-sm text-violet-900">
          <strong>Reopened</strong> because {(generated || []).filter((g) => g.status === 'cancelled').map((g) => g.po_number).join(', ')} was cancelled.
          Only the lines marked <span className="font-semibold">to send</span> need a decision: send them to a supplier, or remove them.
          Once none are left the order goes back to approved.
        </div>
      )}

      {!readOnly && (
        <div className="mb-6">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-400">Assign supplier per item</p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr><th className="px-4 py-2.5">Item</th><th className="px-4 py-2.5 text-right">Qty</th><th className="px-4 py-2.5">Supplier</th><th className="px-4 py-2.5">Pack size</th><th className="px-4 py-2.5 text-right">Unit price</th><th className="px-4 py-2.5 text-right">Remove</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map((l) => {
                  const sid = choice[l.id];
                  const sup = l.available_suppliers.find((s) => s.supplier_id === sid);
                  const locked = !!sentFor(sid);
                  const gone = !!l.removed_at;
                  const showing = openLine === l.id;
                  return (
                    <Fragment key={l.id}>
                    <tr className={gone ? 'bg-gray-50 text-gray-400' : reopened && !locked ? 'bg-violet-50/60' : undefined}>
                      <td className="px-4 py-2.5 text-gray-800">
                        {/* The item name opens the stock lookup. Nothing on this
                            screen used to say whether we already had the thing. */}
                        <button
                          type="button"
                          onClick={() => setOpenLine(showing ? null : l.id)}
                          className={`text-left ${gone ? 'text-gray-400 line-through' : 'text-gray-800 hover:text-brand hover:underline'}`}
                        >
                          {l.name}
                        </button>
                        {gone && (
                          <span className="ml-2 text-xs font-medium text-rose-600">
                            removed{l.removed_by_name ? ` by ${l.removed_by_name}` : ''}
                          </span>
                        )}
                        {l.added_at && (
                          <span className="ml-2 text-xs font-medium text-sky-700">
                            added{l.added_by_name ? ` by ${l.added_by_name}` : ''}
                          </span>
                        )}
                        {reopened && !gone && !locked && (
                          <span className="ml-2 rounded bg-violet-100 px-1.5 py-0.5 text-[11px] font-semibold text-violet-800">to send</span>
                        )}
                        {/* Both badges can show at once and neither hides the
                            other. Tool bags on ORD-00026 are exactly that case:
                            Swindon has the 3 it ordered, AND Bradford is sitting
                            on 73 against an average of 13 — the second is worth
                            knowing even though the first says "no need to buy". */}
                        {/* Stacked rather than side by side: they are two
                            separate findings, not one label split in two, and
                            on a long item name the pills used to wrap into an
                            unreadable jumble. A coloured left edge carries the
                            same meaning the pill colour did, but each line then
                            reads as its own statement. */}
                        {!gone && l.stock && (l.stock.covered_here || l.stock.surplus_elsewhere) ? (
                          <div className="mt-1 max-w-xs space-y-1">
                            {l.stock.covered_here ? (
                              <div className="rounded-r border-l-2 border-amber-400 bg-amber-50 px-2 py-1">
                                <div className="text-[11px] font-semibold text-amber-900">No need to buy</div>
                                <div className="text-[11px] text-amber-800">
                                  {order.delivery_location} already holds {l.stock.at_delivery}
                                </div>
                              </div>
                            ) : null}
                            {l.stock.surplus_elsewhere ? (
                              <div className="rounded-r border-l-2 border-sky-400 bg-sky-50 px-2 py-1">
                                <div className="text-[11px] font-semibold text-sky-900">
                                  Could transfer from {l.stock.best_other.name}
                                </div>
                                <div className="text-[11px] text-sky-800">
                                  {l.stock.best_other.qty} there · {l.stock.average_per_storeroom} average a storeroom
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        {/* Always say what the delivery storeroom holds (06/10/2026): the
                            badges above only appear when it covers the order or another
                            site has a surplus, so a partial holding (Kent 12 of 20) used
                            to vanish until the line was clicked. */}
                        {!gone && l.stock && !l.stock.covered_here ? (
                          <div className="mt-1 text-[11px] text-gray-500">
                            {Number(l.stock.at_delivery) > 0
                              ? <><span className="font-semibold text-gray-700">{order.delivery_location} holds {Number(l.stock.at_delivery)}</span> of {Number(l.quantity)}</>
                              : <>None at {order.delivery_location}</>}
                            {Number(l.stock.total_on_hand) - Number(l.stock.at_delivery) > 0
                              ? ` · ${Number(l.stock.total_on_hand) - Number(l.stock.at_delivery)} elsewhere`
                              : ''}
                          </div>
                        ) : null}
                        {/* Was plain text that looked like a link (06/10/2026); now opens
                            the same panel as the item name. */}
                        {!gone && (
                          <button
                            type="button"
                            onClick={() => setOpenLine(showing ? null : l.id)}
                            className="block text-[11px] font-medium text-brand hover:text-brand-dark hover:underline"
                          >
                            {showing ? 'Hide stock' : 'Check stock across storerooms'}
                          </button>
                        )}
                      </td>
                      <td className={`px-4 py-2.5 text-right tabular-nums${gone ? ' line-through' : ''}`}>
                        {gone || locked ? l.quantity : (
                          <QtyCell
                            orderId={order.id}
                            line={l}
                            onDone={setFlash}
                          />
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {gone ? <span className="text-xs text-gray-400">—</span> : (
                          <select value={sid || ''} disabled={locked} onChange={(e) => setChoice((c) => ({ ...c, [l.id]: Number(e.target.value) }))} className="rounded-md border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100">
                            {l.available_suppliers.length === 0 && <option value="">no supplier linked</option>}
                            {l.available_suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.supplier_name} — {money(s.unit_price)}{s.min_order_qty ? ` · min ${s.min_order_qty}` : ''}{s.is_preferred ? ' ★' : ''}</option>)}
                          </select>
                        )}
                        {/* Warning only - the SKU manager's minimum for this supplier. */}
                        {!gone && !locked && sup?.min_order_qty && Number(l.quantity) < Number(sup.min_order_qty) ? (
                          <div className="mt-1 max-w-[16rem] rounded-r border-l-2 border-amber-400 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-900">
                            {`Below ${sup.supplier_name}'s minimum order of ${sup.min_order_qty} (ordering ${l.quantity})`}
                          </div>
                        ) : null}
                      </td>
                      {/* Units in one of the chosen supplier's packs (SKU manager). */}
                      <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                        {gone || !sup ? '' : Number(sup.pack_size) > 1 ? `Pack of ${Number(sup.pack_size)}` : sup.pack_size != null ? 'Single' : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{gone ? '' : money(sup?.unit_price)}</td>
                      <td className="px-4 py-2.5 text-right">
                        {/* A line already on a sent PO cannot be pulled back here -- removing
                            it would not unsend it, only hide it from this screen. */}
                        {locked ? (
                          <span className="text-xs text-gray-400">sent</span>
                        ) : (
                          <button
                            type="button"
                            disabled={removeLine.isPending}
                            onClick={() => toggleLine(l)}
                            className={`rounded-md border px-2 py-1 text-xs font-medium disabled:opacity-50 ${gone
                              ? 'border-gray-300 text-gray-600 hover:bg-gray-100'
                              : 'border-rose-300 text-rose-600 hover:bg-rose-50'}`}
                          >
                            {gone ? 'Put back' : 'Remove'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {showing ? (
                      <tr>
                        <td colSpan={6} className="bg-gray-50 p-0">
                          <Availability lineId={l.id} />
                        </td>
                      </tr>
                    ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            <AddItem orderId={order.id} onDone={setFlash} />
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-400">PO details — applied to every purchase order below, unless a line overrides it</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-500">Requester</span>
              <select value={requester} onChange={(e) => setRequester(e.target.value)} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm">
                <option value="">— select requester —</option>
                {REQUESTERS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-500">Contract</span>
              <select value={contract} onChange={(e) => setContract(e.target.value)} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm">
                <option value="">— none —</option>
                {(tracking?.contracts || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-500">Account</span>
              <select value={account} onChange={(e) => setAccount(e.target.value)} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm">
                <option value="">— from each item —</option>
                {(tracking?.accounts || []).map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-500">Area</span>
              <select value={area} onChange={(e) => setArea(e.target.value)} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm">
                <option value="">— none —</option>
                {(tracking?.areas || []).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
          </div>
        </div>
      )}

      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-400">{readOnly ? 'Purchase orders' : `${pos.length} purchase order${pos.length === 1 ? '' : 's'} — send each to ApprovalMax`}</p>
        <p className="text-sm text-gray-500">Total across all POs: <span className="font-bold text-gray-900">{money(grandTotal)}</span></p>
      </div>

      <div className="space-y-4">
        {pos.map((po) => {
          const gen = sentFor(po.supplier_id);
          const isSending = sendingSupplier === po.supplier_id;
          return (
            <div key={po.supplier_id} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-gray-50 px-5 py-3">
                <div>
                  <span className="text-xs uppercase tracking-wide text-gray-400">Supplier</span>
                  <div className="text-base font-semibold text-gray-900">{po.supplier_name}</div>
                  {gen?.po_number && <div className="font-mono text-xs text-gray-500">{gen.po_number}</div>}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right"><span className="block text-xs uppercase tracking-wide text-gray-400">Date</span><span className="text-sm text-gray-700">{today}</span></div>
                  {gen ? (
                    <>
                      {gen.no_approval ? (
                        <span title={`Created without approval by ${gen.no_approval_by_name || 'an approver'} - not sent to Xero or ApprovalMax`} className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-3 py-1.5 text-sm font-semibold text-amber-800"><CheckCircleIcon className="h-4 w-4" /> No approval - call-off {gen.call_off_ref}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-3 py-1.5 text-sm font-semibold text-emerald-800"><CheckCircleIcon className="h-4 w-4" /> Sent to ApprovalMax</span>
                      )}
                      {gen.status !== 'cancelled' ? (
                        <button type="button"
                          onClick={async () => {
                            setFlash(null);
                            try { await downloadPoPdf(order.id, gen); } catch { setFlash({ ok: false, msg: `Could not download ${gen.po_number}.` }); }
                          }}
                          title="Download this PO as a PDF to send the supplier"
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                          <ArrowDownTrayIcon className="h-4 w-4" /> Download PO
                        </button>
                      ) : null}
                      {gen.status === 'sent' ? (
                        <button type="button" onClick={() => setCancelFor((v) => (v === gen.id ? null : gen.id))} disabled={cancelPo.isPending}
                          className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-3 py-1.5 text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                          <XCircleIcon className="h-4 w-4" /> {cancelPo.isPending ? 'Cancelling…' : 'Cancel PO'}
                        </button>
                      ) : null}
                    </>
                  ) : !readOnly ? (
                    <>
                      <button type="button" onClick={() => setNoApprovalOpen((v) => (v === po.supplier_id ? null : po.supplier_id))} disabled={isSending || send.isPending}
                        title="For a call-off against an order that is already approved: creates the PO for Goods Receiving without sending it to Xero or ApprovalMax"
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        No approval required
                      </button>
                      <button type="button" onClick={() => sendSupplier(po)} disabled={isSending || send.isPending || !requester || !(poMotivation[po.supplier_id] || '').trim()} title={!requester ? "Select a requester first" : !(poMotivation[po.supplier_id] || '').trim() ? 'Add a motivation first' : undefined} className="inline-flex items-center gap-1 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50">
                        <PaperAirplaneIcon className="h-4 w-4" /> {isSending ? 'Sending…' : 'Send to ApprovalMax'}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
              {gen && cancelFor === gen.id ? (
                <div className="border-b border-rose-200 bg-rose-50 px-5 py-3">
                  <p className="mb-2 text-sm text-rose-900">
                    <strong>Cancel {gen.po_number}?</strong>{' '}
                    {gen.no_approval ? 'It was never sent to Xero.' : 'It will be deleted in Xero.'} This can&apos;t be undone.
                    What should happen to its {po.lines.length} line{po.lines.length === 1 ? '' : 's'}?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => cancelSentPo(po, gen, true)} disabled={cancelPo.isPending}
                      className="rounded-md bg-rose-600 px-3 py-2 text-left text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
                      Won&apos;t be ordered - remove the lines
                      <span className="block text-xs font-normal text-rose-100">The rest of the order stays as it is</span>
                    </button>
                    <button type="button" onClick={() => cancelSentPo(po, gen, false)} disabled={cancelPo.isPending}
                      className="rounded-md border border-rose-300 bg-white px-3 py-2 text-left text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50">
                      Send them again
                      <span className="block text-xs font-normal text-rose-500">Reopens the order so they can be changed or sent to another supplier</span>
                    </button>
                    <button type="button" onClick={() => setCancelFor(null)} className="rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-100">Keep the PO</button>
                  </div>
                </div>
              ) : null}
              {!gen && !readOnly && noApprovalOpen === po.supplier_id ? (
                <div className="border-b border-amber-200 bg-amber-50 px-5 py-3">
                  <p className="mb-2 text-xs text-amber-900">
                    <strong>No approval required</strong> - for a call-off against an order that has already been approved.
                    The PO is created for Goods Receiving straight away and is <strong>not</strong> sent to Xero or ApprovalMax.
                  </p>
                  <div className="flex flex-wrap items-end gap-2">
                    <label className="block min-w-[16rem] flex-1">
                      <span className="mb-1 block text-xs font-medium text-gray-700">Call-off reference <span className="text-rose-600">*</span> <span className="font-normal text-gray-400">(the approved PO or agreement this is called off)</span></span>
                      <input value={callOffRef[po.supplier_id] || ''} onChange={(e) => setCallOffRef((m) => ({ ...m, [po.supplier_id]: e.target.value }))} maxLength={120} placeholder="e.g. PO-00041-1 or the supplier's agreement number" className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm" />
                    </label>
                    <button type="button" onClick={() => sendSupplier(po, { noApproval: true })}
                      disabled={isSending || send.isPending || (callOffRef[po.supplier_id] || '').trim().length < 2 || !(poMotivation[po.supplier_id] || '').trim()}
                      title={(callOffRef[po.supplier_id] || '').trim().length < 2 ? 'Enter the call-off reference first' : !(poMotivation[po.supplier_id] || '').trim() ? 'Add a motivation first' : undefined}
                      className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50">
                      {isSending ? 'Creating…' : 'Create PO without approval'}
                    </button>
                    <button type="button" onClick={() => setNoApprovalOpen(null)} className="rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
                  </div>
                </div>
              ) : null}
              {!gen && po.lines.some((l) => l.min && Number(l.quantity) < Number(l.min)) ? (
                <div className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-xs text-amber-900">
                  <strong>{`Below ${po.supplier_name}'s minimum order:`}</strong>{' '}
                  {po.lines.filter((l) => l.min && Number(l.quantity) < Number(l.min)).map((l) => `${l.name} (${l.quantity} of min ${l.min})`).join(' · ')}
                </div>
              ) : null}
              {!gen && (generated || []).filter((g) => g.supplier_id === po.supplier_id && g.status === 'cancelled').slice(-1).map((g) => (
                <div key={g.id} className="border-b border-rose-200 bg-rose-50 px-5 py-2 text-xs text-rose-800">
                  <strong>{g.po_number}</strong>
                  {` was cancelled${g.cancel_source === 'xero' ? ' in Xero / ApprovalMax' : g.cancelled_by_name ? ` by ${g.cancelled_by_name}` : ''}${g.cancelled_at ? ` on ${new Date(g.cancelled_at).toLocaleDateString('en-GB')}` : ''}. Its lines can be changed and sent again.`}
                </div>
              ))}
              {gen?.motivation ? (
                <div className="border-b border-gray-200 px-5 py-2 text-xs text-gray-600">
                  <span className="font-semibold text-gray-700">Motivation: </span>{gen.motivation}
                </div>
              ) : null}
              {!readOnly && !gen && (
                <div className="border-b border-gray-200 px-5 py-3">
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-700">Motivation <span className="text-rose-600">*</span> <span className="font-normal text-gray-400">(required - the first line on {po.supplier_name}&apos;s PO in Xero and ApprovalMax)</span></span>
                    <textarea value={poMotivation[po.supplier_id] || ''} onChange={(e) => setPoMotivation((m) => ({ ...m, [po.supplier_id]: e.target.value }))} rows={2} maxLength={2000} placeholder="Why is this being bought?" className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm" />
                  </label>
                </div>
              )}
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                  <tr><th className="px-5 py-2">Item</th><th className="px-5 py-2">Contract</th><th className="px-5 py-2">Account</th><th className="px-5 py-2 text-right">Qty</th><th className="px-5 py-2 text-right">Unit price</th><th className="px-5 py-2 text-right">Amount</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {po.lines.map((l) => (
                    <tr key={l.id}>
                      <td className="px-5 py-2 text-gray-800">{l.name}</td>
                      <td className="px-5 py-2">
                        {readOnly ? (
                          <span className="text-gray-500">{lineContracts[l.id] || contract || '—'}</span>
                        ) : (
                          <select
                            value={lineContracts[l.id] || ''}
                            onChange={(e) => setLineContracts((m) => ({ ...m, [l.id]: e.target.value }))}
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs">
                            <option value="">{contract ? `${contract} (PO default)` : '— none —'}</option>
                            {(tracking?.contracts || []).map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        )}
                      </td>
                      <td className="px-5 py-2">
                        {readOnly ? (
                          <span className="text-gray-500">{accountLabel(lineAccounts[l.id] || account || l.catalogAccount) || '—'}</span>
                        ) : (
                          <select
                            value={lineAccounts[l.id] || ''}
                            onChange={(e) => setLineAccounts((m) => ({ ...m, [l.id]: e.target.value }))}
                            className={`w-full rounded-md border px-2 py-1 text-xs ${!(account || l.catalogAccount) && !lineAccounts[l.id] ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}
                            title={!(account || l.catalogAccount) && !lineAccounts[l.id] ? 'This item has no account in the catalogue - pick one so the line is coded in Xero' : undefined}>
                            <option value="">{account ? `${accountLabel(account)} (PO default)` : l.catalogAccount ? `${accountLabel(l.catalogAccount)} (item)` : '— none —'}</option>
                            {/* The default is already the first option; listing it again showed it twice. */}
                            {(tracking?.accounts || []).filter((a) => a.code !== (account || l.catalogAccount)).map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
                          </select>
                        )}
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums">{l.quantity}</td>
                      <td className="px-5 py-2 text-right tabular-nums text-gray-600">{money(l.unit)}</td>
                      <td className="px-5 py-2 text-right tabular-nums text-gray-900">{money(Number(l.unit || 0) * l.quantity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-end border-t border-gray-200 px-5 py-2.5 text-sm"><span className="text-gray-500">Total&nbsp;&nbsp;</span><span className="w-28 text-right font-bold tabular-nums text-gray-900">{money(po.subtotal)}</span></div>
              {!readOnly && !gen && (
                <div className="border-t border-gray-200 px-5 py-3">
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-500">Comment for this PO <span className="font-normal text-gray-400">(added as a note line on {po.supplier_name}&apos;s PO)</span></span>
                    <textarea value={poComments[po.supplier_id] || ''} onChange={(e) => setPoComments((m) => ({ ...m, [po.supplier_id]: e.target.value }))} rows={2} placeholder="Optional note for this supplier…" className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm" />
                  </label>
                </div>
              )}
            </div>
          );
        })}
        {pos.length === 0 && <p className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-400">Assign suppliers to preview the purchase orders.</p>}
      </div>

      {!readOnly && (
        <div className="mt-5">
          {!rejecting ? (
            <button type="button" onClick={() => setRejecting(true)} className="rounded-md border border-rose-300 px-4 py-2 text-sm font-medium text-rose-700"><XCircleIcon className="mr-1 inline h-4 w-4" />Reject order</button>
          ) : (
            <div className="max-w-md space-y-2">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for rejection" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <button type="button" onClick={doReject} disabled={reject.isPending} className="rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Confirm reject</button>
                <button type="button" onClick={() => setRejecting(false)} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The queue was fixed to 'submitted', so an order vanished from the platform
// the moment it was approved -- the only place left to see what was ordered was
// the database. These are the states worth looking back at.
// Reopened = awaiting approval again because a PO on it was cancelled; kept
// apart from new orders so it is obvious which still need pushing (TKT-00117).
const TABS = [
  { key: 'submitted', label: 'Awaiting approval' },
  { key: 'reopened', label: 'Reopened' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: '', label: 'All' },
];

function ProcurementApprovals() {
  const [tab, setTab] = useState('submitted');
  const { data: queue } = useApprovalQueue(tab);
  // Counted on the tab itself, so reopened orders are not out of sight.
  const { data: reopenedQueue } = useApprovalQueue('reopened');
  const [selected, setSelected] = useState(null);

  return (
    <div className="p-6">
      {selected ? (
        <OrderDetail orderId={selected} onBack={() => setSelected(null)} />
      ) : (
        <>
          <h1 className="text-2xl font-bold text-gray-900">Procurement Approvals</h1>
          <p className="mt-1 text-sm text-gray-500">Review submitted stock orders, assign a supplier to each item, and send each supplier’s purchase order to ApprovalMax.</p>
          <div className="mb-3 mt-5 flex flex-wrap gap-1.5">
            {TABS.map((t) => (
              <button
                key={t.key || 'all'}
                type="button"
                onClick={() => setTab(t.key)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold ${tab === t.key
                  ? 'bg-brand text-white'
                  : 'border border-gray-300 text-gray-600 hover:border-brand hover:text-brand'}`}
              >
                {t.label}
                {t.key === 'reopened' && reopenedQueue?.length ? (
                  <span className={`ml-1.5 rounded-full px-1.5 text-[10px] ${tab === t.key ? 'bg-white/25' : 'bg-violet-100 text-violet-800'}`}>{reopenedQueue.length}</span>
                ) : null}
              </button>
            ))}
            <span className="ml-auto flex items-center text-xs text-gray-400">{queue?.length || 0} order{queue?.length === 1 ? '' : 's'}</span>
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr><th className="px-4 py-2.5">Reference</th><th className="px-4 py-2.5">Raised by</th><th className="px-4 py-2.5">Deliver to</th><th className="px-4 py-2.5">Supplier</th><th className="px-4 py-2.5 text-right">Items</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Submitted</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(queue || []).map((o) => (
                  <tr key={o.id} onClick={() => setSelected(o.id)} className="cursor-pointer hover:bg-brand-light/40">
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-600">{o.reference}</td>
                    <td className="px-4 py-2.5 text-gray-800">{o.requested_by_name}</td>
                    <td className="px-4 py-2.5 text-gray-600">{o.delivery_location || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-700" title={(o.suppliers || []).join(', ')}>
                      {(o.suppliers || []).length
                        ? <>{o.suppliers.slice(0, 2).join(', ')}{o.suppliers.length > 2 ? <span className="text-gray-400"> +{o.suppliers.length - 2} more</span> : null}</>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{o.line_count}</td>
                    <td className="px-4 py-2.5">
                      {o.status === 'submitted' && Number(o.reopened) ? (
                        <>
                          <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800">reopened</span>
                          <span className="mt-0.5 block text-[11px] text-gray-500" title={o.cancelled_pos ? `Cancelled: ${o.cancelled_pos}` : undefined}>
                            PO cancelled · {Number(o.to_send_count)} line{Number(o.to_send_count) === 1 ? '' : 's'} to send
                          </span>
                        </>
                      ) : (
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${o.status === 'submitted' ? 'bg-amber-100 text-amber-800' : o.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : o.status === 'rejected' ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-600'}`}>{o.status}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">{o.submitted_at ? new Date(o.submitted_at).toLocaleDateString('en-GB') : ''}</td>
                  </tr>
                ))}
                {queue && queue.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Nothing here.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default ProcurementApprovals;
